import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ShieldCheck, ChevronLeft, ChevronRight, CheckCircle2, Circle, ClipboardList, AlertTriangle, Languages } from "lucide-react";
import { pub, STEP_META, OnbLangProvider, useLang, useL } from "./onboarding/shared";
import { StepPersonal, StepAddress, StepEmergency } from "./onboarding/StepsBasic";
import { StepDeposit, StepPay } from "./onboarding/StepsPay";
import { StepW4, StepDE4 } from "./onboarding/StepsTax";
import { StepI9 } from "./onboarding/StepsI9";
import { StepEEO, StepWOTC } from "./onboarding/StepsVoluntary";
import { StepReview } from "./onboarding/StepReview";

/* 公开页 /onboard/:token — 新员工入职自助（多步向导）。默认纯英文，可切换「English + 中文」对照；不预填 / 不显示招聘侧个人信息 */
export default function OnboardingPortal() {
  return <OnbLangProvider><Portal /></OnbLangProvider>;
}

function LangToggle() {
  const { mode, setMode } = useLang();
  const btn = (m, label, testid) => (
    <button type="button" data-testid={testid} aria-pressed={mode === m} onClick={() => setMode(m)}
      className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${mode === m ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>{label}</button>
  );
  return (
    <div className="flex items-center gap-1 rounded-full bg-white/10 p-0.5 flex-shrink-0" data-testid="onb-lang-toggle" data-mode={mode}>
      <Languages className="h-3.5 w-3.5 text-slate-300 ml-1.5" />
      {btn("en", "English", "onb-lang-en")}
      {btn("bi", "English + 中文", "onb-lang-bi")}
    </div>
  );
}

function Portal() {
  const { token } = useParams();
  const { L, bi } = useL();
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subErr, setSubErr] = useState("");
  const step = STEP_META[idx];
  const locked = rec && ["submitted", "approved"].includes(rec.status);

  const load = useCallback(() => pub.get(token).then((d) => {
    setRec(d);
    const first = STEP_META.findIndex((m) => m.k !== "review" && !(d.completedSteps || []).includes(m.k));
    setIdx(["submitted", "approved"].includes(d.status) ? STEP_META.length - 1 : (first < 0 ? STEP_META.length - 1 : first));
  }).catch((e) => setErr(e.message)), [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (rec && step.k !== "review") setForm({ ...(rec.steps?.[step.k] || {}) }); setSaveErr(""); }, [rec, idx]); // eslint-disable-line

  const save = async (next = true) => {
    setSaving(true); setSaveErr("");
    try {
      const r = await pub.step(token, step.k, form);
      setRec((x) => ({ ...x, completedSteps: r.completedSteps, status: x.status === "sent" ? "in_progress" : x.status, steps: { ...x.steps, [step.k]: r.step } }));
      if (next) setIdx((i) => Math.min(i + 1, STEP_META.length - 1));
    } catch (e) { setSaveErr(e.message); }
    setSaving(false);
  };
  const submit = async (body) => {
    setSubmitting(true); setSubErr("");
    try { await pub.submit(token, body); await load(); } catch (e) { setSubErr(e.message); }
    setSubmitting(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center" data-testid="onb-error">{err}</div>;
  if (!rec) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />Loading onboarding packet…</div>;

  const body = () => {
    const props = { form, setForm, rec, token, disabled: locked };
    switch (step.k) {
      case "personal": return <StepPersonal {...props} />;
      case "address": return <StepAddress {...props} />;
      case "emergency": return <StepEmergency {...props} />;
      case "deposit": return <StepDeposit {...props} />;
      case "pay": return <StepPay {...props} />;
      case "w4": return <StepW4 {...props} />;
      case "de4": return <StepDE4 {...props} />;
      case "i9": return <StepI9 {...props} />;
      case "eeo": return <StepEEO {...props} />;
      case "wotc": return <StepWOTC {...props} questions={rec.wotcQuestions} />;
      default: return <StepReview rec={rec} goto={(k) => setIdx(STEP_META.findIndex((m) => m.k === k))} onSubmit={submit} submitting={submitting} error={subErr} />;
    }
  };
  const meta = [rec.jobTitle, bi ? `${rec.branchNameEn || rec.branchName}${rec.branchName && rec.branchName !== rec.branchNameEn ? ` ${rec.branchName}` : ""}` : (rec.branchNameEn || rec.branchName), rec.hireDate ? `${bi ? "Start 入职日" : "Start"} ${rec.hireDate}` : ""].filter(Boolean).join(" · ");

  return (
    <div className="min-h-screen bg-slate-100" data-testid="onb-page" data-status={rec.status} data-lang={bi ? "bi" : "en"}>
      <div className="bg-slate-900 text-white px-4 sm:px-5 py-3 sm:py-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <ClipboardList className="h-5 w-5 text-red-500 flex-shrink-0" />
        <div className="min-w-0 flex-1 basis-[200px]">
          <p className="font-bold truncate" data-testid="onb-title">{bi ? "ALIGCUS 阿里建筑 · New Hire Onboarding 入职信息表" : "ALIGCUS · New Hire Onboarding"}</p>
          <p className="text-[11px] text-slate-300 truncate" data-testid="onb-meta">{meta}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto sm:ml-auto">
          <LangToggle />
          <span className="ml-auto sm:ml-0 text-[11px] rounded-full bg-white/10 px-2.5 py-1 whitespace-nowrap" data-testid="onb-progress">{(rec.completedSteps || []).length}/{STEP_META.length - 1} {L("steps", "步")}</span>
        </div>
      </div>
      <div className="max-w-[1080px] mx-auto p-3 sm:p-5 grid grid-cols-1 md:grid-cols-[230px_1fr] gap-4">
        <aside className="md:sticky md:top-4 self-start">
          <ol className="flex md:flex-col gap-1 overflow-x-auto pb-1" data-testid="onb-stepper">
            {STEP_META.map((m, i) => {
              const done = (rec.completedSteps || []).includes(m.k);
              return (
                <li key={m.k}>
                  <button type="button" data-testid={`onb-nav-${m.k}`} onClick={() => setIdx(i)} className={`w-full text-left flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[12.5px] whitespace-nowrap ${i === idx ? "bg-red-600 text-white" : done ? "text-emerald-700 hover:bg-white" : "text-slate-500 hover:bg-white"}`}>
                    {done ? <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> : <Circle className="h-3.5 w-3.5 flex-shrink-0" />}<span className="truncate">{i + 1}. {m.en}{bi && <span className={`ml-1 ${i === idx ? "text-white/70" : "text-slate-400"}`}>{m.zh}</span>}</span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
        <main className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 min-w-0">
          {locked ? (
            <div className="text-center py-8" data-testid="onb-done">
              <ShieldCheck className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-slate-800">{rec.status === "approved" ? L("Approved by HR — welcome aboard!", "HR 已审核通过，欢迎加入！") : L("Packet submitted — thank you!", "已提交，感谢！")}</p>
              <p className="text-sm text-slate-500 mt-1">{L(`Your W-4, DE-4 and I-9 Section 1 were signed and archived${rec.submittedAt ? ` on ${rec.submittedAt.slice(0, 19).replace("T", " ")} UTC` : ""}. HR will review your packet and complete I-9 Section 2 on your first day with your original documents.`, "您的 W-4、DE-4 与 I-9 第 1 部分已签署归档。HR 将审核本表，并在您入职首日凭原件完成 I-9 第 2 部分。")}</p>
              {rec.recordHash && <p className="text-[10px] text-slate-400 font-mono mt-3 break-all">Record #{rec.recordHash.slice(0, 24)}</p>}
            </div>
          ) : (
            <>
              {rec.status === "returned" && <p className="mb-3 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="onb-returned"><AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /><span>{L("HR returned this packet for correction:", "HR 退回修改：")} <b>{rec.returnReason}</b>. {L("Please update and resubmit.", "请更正后重新提交。")}</span></p>}
              <h2 className="text-base md:text-lg font-extrabold text-slate-800" data-testid="onb-step-title">{idx + 1}. {step.en} {bi && <span className="text-slate-400 font-medium text-sm">{step.zh}</span>}</h2>
              <div className="mt-4">{body()}</div>
              {step.k !== "review" && (
                <div className="mt-6 flex items-center gap-2 border-t border-slate-100 pt-4">
                  <button type="button" data-testid="onb-back" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)} className="inline-flex items-center gap-1 text-[13px] rounded-full border border-slate-300 px-4 py-2 disabled:opacity-40"><ChevronLeft className="h-4 w-4" />{L("Back", "上一步")}</button>
                  {saveErr && <span data-testid="onb-save-error" className="text-[12px] text-red-600 truncate">{saveErr}</span>}
                  <button type="button" data-testid="onb-save-next" disabled={saving} onClick={() => save(true)} className="ml-auto inline-flex items-center gap-1 text-[13px] font-semibold rounded-full bg-red-600 hover:bg-red-700 text-white px-5 py-2 disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{L("Save & continue", "保存并继续")}<ChevronRight className="h-4 w-4" /></button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
